import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    EmployeeLimitModal,
    Pagination,
    QRCodeDownload,
    ReactSelect,
    Spinner,
    TooltipCustom,
    TooltipPopover,
} from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../services/ErrorService";
import Form from "react-bootstrap/Form";
import { Link, useLocation, useParams } from "react-router-dom";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import ToastComponent from "../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../hooks/useToast";
import {
    useGetBookingAllowedDurationsQuery,
    useGetOnlineBookingsConfigQuery,
    useUpdateBranchOnlineBookingSettingsMutation,
    useUpdateCompanyOnlineBookingSettingsMutation,
} from "@me-team/host/main/onlineBookings/graphql/onlineBookings.hooks";
import {
    BookingAllowedDurationProxy,
    BranchProxy,
    DateTimePeriodType,
    OnlineBookingSettings,
} from "@me-team/host/main/graphql/types";
import JoyrideWrapper from "../Joyride/JoyrideWrapper";
import SuccessfulModal from "../Joyride/modals/SuccessfulModal";
import { AllModulesTagName } from "../../utils/enums";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { OptionString } from "../../interfaces/interfaces";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import ConnectModal from "../Joyride/modals/ConnectModal";
import AddPreTrainingModuleModal from "../MainMenu/AddPreTrainingModuleModal";
import StartTutorialButton from "@services/ui-components/src/buttons/StartTutorialButton/StartTutorialButton";
import { useCloseTooltipsOnModalOpen } from "../../hooks/useCloseTooltipsOnModalOpen";
import { useTutorialAccess } from "../Joyride/useTutorialAccess";

interface BranchRegistrations {
    [key: number]: boolean;
}

interface OnlineBookingSettingsTableProps {
    canEditOnlineBookingSettings: boolean;
}

const basePath = process.env.REACT_APP_POS_URL;
const bookingBasePath = process.env.REACT_APP_BOOKING_URL;

const OnlineBookingSettingsTable: React.FC<OnlineBookingSettingsTableProps> = ({ canEditOnlineBookingSettings }) => {
    const { t, i18n } = useTranslation();
    const { branchId } = useParams();
    const location = useLocation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const INDONESIAN_LANGUAGE = "id";
    const isIndonesianLanguage = i18n.language === INDONESIAN_LANGUAGE;
    const [, copy] = useCopyToClipboard();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const runJoy = localStorage.getItem("run") === "true";
    const step = localStorage.getItem("step") || "0";
    const [stepIndex, setStepIndex] = useState(+step);
    const [run, setRun] = useState(runJoy);
    const [modalJoyride, setModalJoyride] = useState(false);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const checkRefneedsClientRecall = useRef(null);
    const [bookingAllowedDuration, setBookingAllowedDuration] = useState<OptionString>(null);
    const [isOpenAddPreTrainingModuleModal, setOpenAddPreTrainingModuleModal] = useState<boolean>(false);
    const [openConnectModal, setOpenConnectModal] = useState<boolean>(false);
    const [showEmployeeLimitModal, setShowEmployeeLimitModal] = useState(false);
    const { tooltipResetKey, resetTooltips } = useCloseTooltipsOnModalOpen();
    const enabledOnlineBookingModule: boolean = useModulesAccess([AllModulesTagName.BOOKING]);
    const { canStartOnlineBookingTutorial } = useTutorialAccess();
    const shouldShowBookingUrlColumn = canEditOnlineBookingSettings;

    const handleLabelClick = () => {
        if (checkRefneedsClientRecall.current) {
            checkRefneedsClientRecall.current.focus();
        }
    };

    const {
        data: onlineBookingSettingsData,
        loading,
        refetch: refetchOnlineBookingSettings,
    } = useGetOnlineBookingsConfigQuery({
        variables: {
            page: currentPage,
            itemsPerPage: itemsPerPage,
            companyId: +currentCompanyId,
        },
        fetchPolicy: "network-only",
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onCompleted: (data) => {
            const allowedDuration = data.user.company.meBookingModuleConfig.allowedDuration;
            const allowedDurationValue = allowedDuration.value as DateTimePeriodType;
            bookingAllowedDurationRef.current = allowedDurationValue;
            initialBookingAllowedDurationRef.current = allowedDurationValue;
            setBookingAllowedDuration({ value: allowedDuration.value, label: allowedDuration.name });
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data: bookingAllowedDurations, loading: loadingBookingAllowedDurations } =
        useGetBookingAllowedDurationsQuery();
    const bookingAllowedDurationOptions =
        bookingAllowedDurations?.bookingAllowedDurations.map((item: BookingAllowedDurationProxy) => ({
            label: item.value,
            value: item.name,
        })) ?? [];
    const selectedBookingDurationOption =
        bookingAllowedDurationOptions.find((option) => option.value === bookingAllowedDuration?.value) ?? null;

    const [updateCompanyOnlineBookingSettings] = useUpdateCompanyOnlineBookingSettingsMutation();
    const [updateBranchOnlineBookingSettings] = useUpdateBranchOnlineBookingSettingsMutation();

    const company = onlineBookingSettingsData && onlineBookingSettingsData?.user?.company;
    const branches = onlineBookingSettingsData?.user?.company?.branchesPaginated?.branches;
    const pageCount = parseInt(company?.branchesPaginated?.pageCount || "0");
    const totalItems = company?.branchesPaginated?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);
    const [confirmationCall, setConfirmationCall] = useState<boolean>(false);
    const [onlineRegistrationsCompany, setOnlineRegistrationsCompany] = useState<boolean>(false);
    const [onlineRegistrationsBranch, setOnlineRegistrationsBranch] = useState<{
        [key: number]: boolean;
    }>({});
    const initialConfirmationCallRef = useRef<boolean>(false);
    const initialOnlineRegistrationsCompanyRef = useRef<boolean>(false);
    const initialOnlineRegistrationsBranchRef = useRef<{ [key: number]: boolean }>({});

    const confirmationCallRef = useRef<boolean>(confirmationCall);
    const onlineRegistrationsCompanyRef = useRef<boolean>(onlineRegistrationsCompany);
    const onlineRegistrationsBranchRef = useRef<{ [key: number]: boolean }>({});
    const modifiedBranchesRef = useRef<Set<number>>(new Set());
    const bookingAllowedDurationRef = useRef<DateTimePeriodType | null>(null);
    const initialBookingAllowedDurationRef = useRef<DateTimePeriodType | null>(null);
    const companyMutationChainRef = useRef<Promise<void>>(Promise.resolve());

    const handleStartTutorial = () => {
        if (!enabledOnlineBookingModule) {
            setOpenAddPreTrainingModuleModal(true);
        } else {
            setOpenConnectModal(true);
        }
    };

    const toggleAddPreTrainingModuleModal = () => {
        setOpenAddPreTrainingModuleModal((prevState) => !prevState);
    };
    const toggleOpenConnectModal = () => {
        setOpenConnectModal((prevState) => !prevState);
    };

    const toggleEmployeeLimitModal = () => {
        setShowEmployeeLimitModal((prevState) => !prevState);
    };

    const hasBranchChanges = useCallback(() => {
        for (const key in onlineRegistrationsBranchRef.current) {
            if (onlineRegistrationsBranchRef.current[key] !== initialOnlineRegistrationsBranchRef.current[key]) {
                return true;
            }
        }
        return false;
    }, []);

    useEffect(() => {
        if (!loading && company && branches) {
            const dirtyStates = { ...onlineRegistrationsBranch };
            const isBranchDirty = hasBranchChanges();
            const isCompanySwitchDirty = hasCompanySwitchChanges();

            const initialConfirmationCall = company.meBookingModuleConfig?.confirmationCall ?? false;
            const initialOnlineRegistrationsCompany = company.meBookingModuleConfig?.onlineBooking ?? false;
            const initialBranchRegistrations = branches.reduce((acc, branch) => {
                acc[branch.id] =
                    isBranchDirty && dirtyStates[branch.id] !== undefined
                        ? dirtyStates[branch.id]
                        : branch.onlineRegistration;
                return acc;
            }, {} as BranchRegistrations);

            if (!isCompanySwitchDirty) {
                setConfirmationCall(initialConfirmationCall);
                setOnlineRegistrationsCompany(initialOnlineRegistrationsCompany);
                initialConfirmationCallRef.current = initialConfirmationCall;
                initialOnlineRegistrationsCompanyRef.current = initialOnlineRegistrationsCompany;
            }

            setOnlineRegistrationsBranch(initialBranchRegistrations);
            initialOnlineRegistrationsBranchRef.current = initialBranchRegistrations;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, company, branches]);

    useEffect(() => {
        confirmationCallRef.current = confirmationCall;
    }, [confirmationCall]);

    useEffect(() => {
        onlineRegistrationsCompanyRef.current = onlineRegistrationsCompany;
    }, [onlineRegistrationsCompany]);

    useEffect(() => {
        onlineRegistrationsBranchRef.current = onlineRegistrationsBranch;
    }, [onlineRegistrationsBranch]);

    useEffect(() => {
        bookingAllowedDurationRef.current = (bookingAllowedDuration?.value as DateTimePeriodType) ?? null;
    }, [bookingAllowedDuration]);

    const getCompanyOnlineBookingSettingsInput = useCallback(
        (): OnlineBookingSettings => ({
            onlineBooking: onlineRegistrationsCompanyRef.current,
            needsClientRecall: confirmationCallRef.current,
            onlineBookingDuration: bookingAllowedDurationRef.current ?? undefined,
        }),
        []
    );

    /** Queues company level saves so each mutation runs after the previous one and older
    snapshots cannot overwrite newer settings **/
    const enqueueCompanyMutation = useCallback((mutationTask: () => Promise<void>): Promise<void> => {
        const previousMutation = companyMutationChainRef.current;

        const queuedMutation: Promise<void> = previousMutation.catch(() => undefined).then(mutationTask);

        companyMutationChainRef.current = queuedMutation;

        return queuedMutation;
    }, []);

    const hasCompanySwitchChanges = useCallback(() => {
        return (
            confirmationCallRef.current !== initialConfirmationCallRef.current ||
            onlineRegistrationsCompanyRef.current !== initialOnlineRegistrationsCompanyRef.current
        );
    }, []);

    const hasBookingDurationChanges = useCallback(() => {
        return bookingAllowedDurationRef.current !== initialBookingAllowedDurationRef.current;
    }, []);

    const hasCompanyChanges = useCallback(() => {
        return hasCompanySwitchChanges() || hasBookingDurationChanges();
    }, [hasCompanySwitchChanges, hasBookingDurationChanges]);

    const handleCompanyMutationChange = useCallback(async () => {
        if (!canEditOnlineBookingSettings) return;

        await enqueueCompanyMutation(async (): Promise<void> => {
            if (!hasCompanyChanges()) return;

            const input = getCompanyOnlineBookingSettingsInput();

            await updateCompanyOnlineBookingSettings({
                variables: { companyId: Number(currentCompanyId), branchId: Number(branchId), input },
                onCompleted: (data) => {
                    if (data) toggleShowToast(t("Saved"));
                    initialConfirmationCallRef.current = confirmationCallRef.current;
                    initialOnlineRegistrationsCompanyRef.current = onlineRegistrationsCompanyRef.current;
                    initialBookingAllowedDurationRef.current = bookingAllowedDurationRef.current;
                },
                context: {
                    errorType: "local",
                },
                onError: (error: ApolloError) => {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                },
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        canEditOnlineBookingSettings,
        updateCompanyOnlineBookingSettings,
        hasCompanyChanges,
        getCompanyOnlineBookingSettingsInput,
        enqueueCompanyMutation,
        toggleShowToast,
        t,
    ]);

    const handleBranchMutations = useCallback(async () => {
        if (!canEditOnlineBookingSettings) return;
        if (modifiedBranchesRef.current.size === 0) return;
        const currentStates = { ...onlineRegistrationsBranchRef.current };
        const branchIdsToUpdate = Array.from(modifiedBranchesRef.current).filter((branchId) => {
            const currentValue = currentStates[branchId];
            return (
                typeof currentValue === "boolean" &&
                currentValue !== initialOnlineRegistrationsBranchRef.current[branchId]
            );
        });

        if (branchIdsToUpdate.length === 0) {
            modifiedBranchesRef.current.clear();
            return;
        }

        for (const branchId of branchIdsToUpdate) {
            const input: OnlineBookingSettings = {
                onlineBooking: currentStates[branchId],
            };

            await updateBranchOnlineBookingSettings({
                variables: { companyId: +currentCompanyId, branchId, input },
                context: {
                    errorType: "local",
                },
                onCompleted: () => {
                    initialOnlineRegistrationsBranchRef.current[branchId] =
                        onlineRegistrationsBranchRef.current[branchId];
                },
                onError: (error: ApolloError) => {
                    setIsErrorModalOpen(ErrorService.errorHandling(error as ApolloError));
                },
            });
        }
        modifiedBranchesRef.current.clear();
        toggleShowToast(t("Saved"));
        refetchOnlineBookingSettings();
    }, [
        canEditOnlineBookingSettings,
        updateBranchOnlineBookingSettings,
        currentCompanyId,
        refetchOnlineBookingSettings,
        t,
        toggleShowToast,
    ]);

    useEffect(() => {
        if (!canEditOnlineBookingSettings) return;

        return () => {
            handleCompanyMutationChange();
            handleBranchMutations();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEditOnlineBookingSettings, location.pathname]);

    useEffect(() => {
        if (!canEditOnlineBookingSettings) return;

        const handleBeforeUnload = () => {
            if (hasCompanyChanges()) {
                const input = getCompanyOnlineBookingSettingsInput();

                const query = `
          mutation UpdateCompanyOnlineBookingSettings($companyId: Int!, $branchId: Int!, $input: OnlineBookingSettings!) {
            updateCompanyOnlineBookingSettings(companyId: $companyId, branchId: $branchId, input: $input)
          }
        `;

                const variables = {
                    companyId: +currentCompanyId,
                    branchId: +branchId,
                    input,
                };

                const payload = JSON.stringify({
                    query,
                    variables,
                });

                fetch(`${basePath}/graphql/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: payload,
                    keepalive: true,
                    credentials: "include",
                });
            }

            if (hasBranchChanges()) {
                modifiedBranchesRef.current.forEach((branchId) => {
                    const onlineBooking = onlineRegistrationsBranchRef.current[branchId];

                    if (
                        typeof onlineBooking === "boolean" &&
                        onlineBooking !== initialOnlineRegistrationsBranchRef.current[branchId]
                    ) {
                        const input: OnlineBookingSettings = {
                            onlineBooking,
                        };

                        const query = `
              mutation UpdateBranchOnlineBookingSettings($companyId: Int!, $branchId: Int!, $input: OnlineBookingSettings!) {
                updateBranchOnlineBookingSettings(companyId: $companyId, branchId: $branchId, input: $input)
              }
            `;

                        const variables = {
                            companyId: +currentCompanyId,
                            branchId,
                            input,
                        };

                        const payload = JSON.stringify({
                            query,
                            variables,
                        });

                        fetch(`${basePath}/graphql/`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: payload,
                            keepalive: true,
                            credentials: "include",
                        });
                    }
                });
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEditOnlineBookingSettings, hasCompanyChanges(), hasBranchChanges(), company, branches, currentCompanyId]);

    const toggleConfirmationCall = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        confirmationCallRef.current = checked;
        setConfirmationCall(checked);
    };

    const toggleOnlineRegistrationCompany = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        onlineRegistrationsCompanyRef.current = checked;
        setOnlineRegistrationsCompany(checked);
    };

    const toggleOnlineRegistrationBranch = (branch: BranchProxy, event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;

        onlineRegistrationsBranchRef.current = {
            ...onlineRegistrationsBranchRef.current,
            [branch.id]: checked,
        };

        setOnlineRegistrationsBranch((prev) => ({
            ...prev,
            [branch.id]: checked,
        }));

        if (checked === initialOnlineRegistrationsBranchRef.current[branch.id]) {
            modifiedBranchesRef.current.delete(branch.id);
            return;
        }

        modifiedBranchesRef.current.add(branch.id);
    };

    const handleCopyToClipboard = async (value: string) => {
        await copy(value);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleChangeSwitches = useCallback(async () => {
        if (!canEditOnlineBookingSettings) return;

        try {
            await Promise.all([handleCompanyMutationChange(), handleBranchMutations()]);
        } catch (error) {
            console.error(error);
        }
    }, [canEditOnlineBookingSettings, handleCompanyMutationChange, handleBranchMutations]);

    useEffect(() => {
        if (!canEditOnlineBookingSettings) return;

        const handleVisibilityChange = async () => {
            if (document.visibilityState === "hidden") {
                if (hasCompanyChanges() || hasBranchChanges()) {
                    try {
                        await handleChangeSwitches();
                    } catch (error) {
                        console.error(error);
                    }
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [canEditOnlineBookingSettings, hasCompanyChanges, hasBranchChanges, handleChangeSwitches]);

    const handleBranchLinkClick = async (e: React.MouseEvent<HTMLAnchorElement>, branchId: number) => {
        if (hasCompanyChanges() || hasBranchChanges()) {
            e.preventDefault();
            await handleChangeSwitches();
            window.open(`${bookingBasePath}/company/${currentCompanyId}/branch-menu-list/${branchId}`, "_blank");
        }
    };

    const bookingDurationHandler = (value: OptionString) => {
        if (!canEditOnlineBookingSettings) return;
        if (bookingAllowedDuration?.value === value.value) return;
        bookingAllowedDurationRef.current = value.value as DateTimePeriodType;
        setBookingAllowedDuration(value);
    };

    if (loading || loadingBookingAllowedDurations) return <Spinner />;

    return (
        <>
            <Row className="my-4 pb-2">
                <Col lg={3} className="online-registration-сall">
                    <Form>
                        <div className="d-flex align-items-center justify-content-start">
                            <Form.Label
                                htmlFor="needsClientRecall"
                                className="mb-0 fs-7 fw-bold me-4 pe-1 col-2"
                                style={{ minWidth: "8.438rem" }}
                                onClick={handleLabelClick}
                            >
                                {t("Call for Confirmation")}
                            </Form.Label>
                            <Form.Check
                                type="switch"
                                id="onlineBookingSettings-needsClientRecall-field"
                                checked={!!confirmationCall}
                                onChange={toggleConfirmationCall}
                                ref={checkRefneedsClientRecall}
                                disabled={!canEditOnlineBookingSettings}
                            />
                            <Form.Label className="mb-0 fs-7 fw-bold" onClick={handleLabelClick}>
                                <TooltipPopover
                                    placement="right"
                                    maxWidth="220px"
                                    text={t(
                                        'Call back for confirmation - enable this function so that through the online registration form, records are created with the status "Approve client", so that the administrator can call back such customers and confirm the record.'
                                    )}
                                >
                                    <i className="bi bi-info-circle text-dark fs-16 ms-2 align-self-center"></i>
                                </TooltipPopover>
                            </Form.Label>
                        </div>
                    </Form>
                </Col>
                <Col md={7} lg={8} xxl={isIndonesianLanguage ? 8 : 6} className={"mt-3 mt-lg-0"}>
                    <Row className="align-items-center justify-content-start justify-content-lg-center">
                        <Col lg="auto" className="pe-0 ps-lg-3 ps-lg-6" style={{ maxWidth: "13rem" }}>
                            <label htmlFor="maximumTermForOnlineBooking" className="mb-0 fs-7 fw-bold form-label">
                                {t("Maximum term for online booking")}
                            </label>
                        </Col>
                        <Col lg="auto" className={"mt-2 mt-lg-0"}>
                            <div className="position-relative w-100" style={{ minWidth: "11rem" }}>
                                <ReactSelect
                                    options={bookingAllowedDurationOptions}
                                    value={selectedBookingDurationOption}
                                    onChange={bookingDurationHandler}
                                    placeholder={""}
                                    isSearchable={false}
                                    name={"maximumTermForOnlineBooking"}
                                    disabled={!canEditOnlineBookingSettings}
                                />

                                <div className="position-absolute top-50 translate-middle-y " style={{ right: "35px" }}>
                                    <TooltipCustom
                                        placement="bottom"
                                        text={
                                            <>
                                                <p className={"mb-3"}>
                                                    {t(
                                                        "Set the maximum period for which clients can make appointments through Online Booking."
                                                    )}
                                                </p>
                                                <p className={"mb-0"}>
                                                    {t(
                                                        "For example, if you choose '2 weeks', clients will only be able to book online within the next 2 weeks."
                                                    )}
                                                </p>
                                            </>
                                        }
                                    >
                                        <i className="bi bi-info-circle i-to-block"></i>
                                    </TooltipCustom>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>
                {canStartOnlineBookingTutorial && (
                    <>
                        <Col
                            md={5}
                            xl={4}
                            xxl={3}
                            className={`${isIndonesianLanguage ? "d-none d-sm-flex d-md-none" : "d-xs-flex d-md-none d-xxl-flex"}  mt-3 mt-lg-0 d-flex justify-content-lg-end`}
                        >
                            <StartTutorialButton
                                classBtn={`w-100 text-truncate 
                        ${isIndonesianLanguage ? "px-lg-1" : ""}
                        `}
                                handleClick={handleStartTutorial}
                            />
                        </Col>
                        <Col
                            md={5}
                            lg={1}
                            className={`
                   ${isIndonesianLanguage ? "d-xs-flex d-sm-none d-md-flex" : "d-none d-md-flex d-xxl-none"}
                     mt-3 mt-lg-0 d-flex align-items-end align-items-lg-center justify-content-lg-end `}
                        >
                            <StartTutorialButton
                                classBtn="w-auto px-1"
                                iconOnly
                                handleClick={handleStartTutorial}
                                tooltipResetKey={tooltipResetKey}
                            />
                        </Col>
                    </>
                )}
            </Row>
            <Row className="mt-1">
                <Col
                    id={"table"}
                    className={run && window?.innerWidth > 630 ? "table-responsive scrollbar " : "overflow-auto"}
                >
                    <div className="table-responsive scrollbar">
                        {company ? (
                            <table className="table">
                                <thead>
                                    <tr className="fs-7">
                                        <th className="py-3 col-2 ps-4 align-middle bg-violet-light">{t("company")}</th>
                                        <th className="py-3 col-1 align-middle bg-violet-light">{t("city")}</th>
                                        <th className="py-3 col-1 align-middle bg-violet-light">{t("address")}</th>
                                        <th className="py-3 col-2 align-middle bg-violet-light">
                                            {t("Online Booking")}
                                        </th>
                                        {shouldShowBookingUrlColumn && (
                                            <th className="py-3 col-2 align-middle bg-violet-light">URL</th>
                                        )}
                                        <th className="py-3 col-2 align-middle bg-violet-light"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="align-middle border-bottom trClass">
                                        <td className="align-middle py-4 ps-4 tr1">{company?.name}</td>
                                        <td className="align-middle py-4 tr2">{company?.city?.name}</td>
                                        <td className="align-middle py-4 tr3">{company?.address}</td>
                                        <td className="align-middle py-4 online-record">
                                            <Form>
                                                <Form.Check
                                                    type="switch"
                                                    id={`online-registration-${currentCompanyId}`}
                                                    label={onlineRegistrationsCompany ? t("allowed") : t("Forbidden")}
                                                    checked={!!onlineRegistrationsCompany}
                                                    onChange={toggleOnlineRegistrationCompany}
                                                    disabled={!canEditOnlineBookingSettings}
                                                />
                                            </Form>
                                        </td>
                                        {shouldShowBookingUrlColumn && (
                                            <td className="align-middle py-4 border-0 url-step">
                                                <Link
                                                    to={`${bookingBasePath}/company/${currentCompanyId}`}
                                                    className="text-grey"
                                                    target="_blank"
                                                >
                                                    <span className="text-grey word-break-all">
                                                        {`${bookingBasePath}/company/${currentCompanyId}`}
                                                    </span>
                                                </Link>
                                                <TooltipCustom placement="bottom" text={t("Copy")}>
                                                    <Button
                                                        variant="link"
                                                        className="p-0 ms-2"
                                                        onClick={() =>
                                                            handleCopyToClipboard(
                                                                `${bookingBasePath}/company/${currentCompanyId}`
                                                            )
                                                        }
                                                    >
                                                        <i className="bi bi-stickies text-primary fs-normal"></i>
                                                    </Button>
                                                </TooltipCustom>
                                            </td>
                                        )}
                                        <td className="align-middle py-4 pe-0 text-end">
                                            <QRCodeDownload
                                                qrCodeUrl={company?.bookingQrLink}
                                                disabled={!canEditOnlineBookingSettings}
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : null}
                        {!!branches?.length ? (
                            <table className="table table-borderless mb-0">
                                <thead>
                                    <tr className="fs-7">
                                        <th className="py-3 col-2 ps-4 align-middle">{t("Branch")}</th>
                                        <th className="py-3 col-1 align-middle">{t("city")}</th>
                                        <th className="py-3 col-1 align-middle">{t("address")}</th>
                                        <th className="py-3 col-2 align-middle">{t("Online Booking")}</th>
                                        {shouldShowBookingUrlColumn && <th className="py-3 col-2 align-middle">URL</th>}
                                        <th className="py-3 col-2 align-middle"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {branches?.map((branch: BranchProxy) => (
                                        <tr key={branch.id} className="align-middle border-bottom">
                                            <td className="align-middle py-4 ps-4">{branch?.name}</td>
                                            <td className="align-middle py-4">{branch?.city?.name}</td>
                                            <td className="align-middle py-4">{branch?.address}</td>
                                            <td
                                                className={`align-middle py-4  ${branch.id === +branchId ? "online-record-branch" : ""}`}
                                            >
                                                <Form>
                                                    <Form.Check
                                                        type="switch"
                                                        id={`online-registration-branch-${branch?.id}`}
                                                        label={
                                                            onlineRegistrationsBranch[branch?.id]
                                                                ? t("allowed")
                                                                : t("Forbidden")
                                                        }
                                                        checked={!!onlineRegistrationsBranch[branch?.id]}
                                                        onChange={(event) =>
                                                            toggleOnlineRegistrationBranch(branch, event)
                                                        }
                                                        disabled={
                                                            !onlineRegistrationsCompany || !canEditOnlineBookingSettings
                                                        }
                                                    />
                                                </Form>
                                            </td>
                                            {shouldShowBookingUrlColumn && (
                                                <td className="align-middle py-4">
                                                    <Link
                                                        to={`${bookingBasePath}/company/${currentCompanyId}/branch-menu-list/${branch?.id}`}
                                                        className="text-grey"
                                                        target="_blank"
                                                        onClick={(e) => handleBranchLinkClick(e, branch?.id)}
                                                    >
                                                        <span className="text-grey d-block word-break-all">
                                                            {`${bookingBasePath}/company/${currentCompanyId}/branch-menu-list/${branch?.id}`}
                                                        </span>
                                                    </Link>
                                                    <TooltipCustom placement="bottom" text={t("Copy")}>
                                                        <Button
                                                            variant="link"
                                                            className="p-0 ms-2"
                                                            onClick={() =>
                                                                handleCopyToClipboard(
                                                                    `${bookingBasePath}/company/${currentCompanyId}/branch-menu-list/${branch?.id}`
                                                                )
                                                            }
                                                        >
                                                            <i className="bi bi-stickies text-primary fs-normal"></i>
                                                        </Button>
                                                    </TooltipCustom>
                                                </td>
                                            )}
                                            <td className="align-middle py-4 pe-0 text-end">
                                                <QRCodeDownload
                                                    qrCodeUrl={branch?.bookingQrLink}
                                                    disabled={!canEditOnlineBookingSettings}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : null}
                    </div>
                </Col>
            </Row>
            {company?.branchesPaginated?.totalCount > 20 && (
                <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            )}
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
            {run && !loading && (
                <JoyrideWrapper
                    run={run}
                    stepIndex={stepIndex}
                    setStepIndex={setStepIndex}
                    setRun={setRun}
                    setModalJoyride={setModalJoyride}
                    mutationForOnlineRecord={handleChangeSwitches}
                />
            )}
            <EmployeeLimitModal
                isCheckLimitBeforeTutorial
                showEmployeeLimitModal={showEmployeeLimitModal}
                handleCloseEmployeeLimitModal={toggleEmployeeLimitModal}
            />
            <ConnectModal
                show={openConnectModal}
                setOpenConnectModal={setOpenConnectModal}
                toggleEmployeeLimitModal={toggleEmployeeLimitModal}
                onClose={() => resetTooltips()}
            />
            <AddPreTrainingModuleModal
                show={isOpenAddPreTrainingModuleModal}
                onClose={toggleAddPreTrainingModuleModal}
                companyId={Number(currentCompanyId)}
                toggleOpenConnectModal={toggleOpenConnectModal}
            />
            <SuccessfulModal setModalJoyride={setModalJoyride} modalJoyride={modalJoyride} />
        </>
    );
};

export default OnlineBookingSettingsTable;
